import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, type LiveSession, type ProbeRequestReceipt, type RelayHealth, type RoomRecord } from "./api";
import { VideoPreview } from "./components/VideoPreview";

type EditorState = { mode: "create" } | { mode: "edit"; room: RoomRecord } | null;

const STATUS_LABEL: Record<RoomRecord["status"], string> = {
  live: "直播中",
  offline: "未开播",
  pending_offline: "等待下播确认",
  error: "状态待迁移",
};

type RoomFilter = "all" | "live" | "offline" | "error";
const DATA_STALE_MS = 10 * 60 * 1_000;

function formatTime(value: string | null, fallback = "尚未检测") {
  if (!value) return fallback;
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

function roomHealth(room: RoomRecord, relay: RelayHealth | null): { label: string; tone: string } {
  if (room.archivedAt) return { label: "已归档", tone: "muted" };
  if (!room.enabled) return { label: "监控停用", tone: "muted" };
  if (relay && !relay.online) return { label: relay.lastSeenAt ? "中继离线" : "中继未连接", tone: "offline" };
  if (room.probeHealth === "error") return { label: "探测异常", tone: "error" };
  if (!room.lastSuccessAt) return { label: "等待确认", tone: "unknown" };
  if (relay?.lastSeenAt && Date.parse(relay.lastSeenAt) - Date.parse(room.lastSuccessAt) > DATA_STALE_MS) {
    return { label: "数据过期", tone: "stale" };
  }
  return { label: "探测正常", tone: "healthy" };
}

const wait = (milliseconds: number) => new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));

function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.login(password);
      onAuthenticated();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "登录失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-panel" aria-labelledby="login-title">
        <div className="radar-mark" aria-hidden="true"><span /></div>
        <p className="brand">LiveRadar</p>
        <h1 id="login-title">进入直播监测台</h1>
        <p className="login-copy">房间状态、场次和临时流地址只对管理员开放。</p>
        <form onSubmit={submit}>
          <input className="visually-hidden" name="username" autoComplete="username" value="admin" readOnly tabIndex={-1} aria-hidden="true" />
          <label htmlFor="password">管理员密码</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoFocus
          />
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="button primary wide" disabled={busy || !password}>
            {busy ? "正在验证…" : "进入监测台"}
          </button>
        </form>
      </section>
    </main>
  );
}

function RoomEditor({ state, onClose, onSaved }: { state: NonNullable<EditorState>; onClose: () => void; onSaved: (room: RoomRecord) => void }) {
  const room = state.mode === "edit" ? state.room : null;
  const [input, setInput] = useState(room?.originUrl ?? "");
  const [alias, setAlias] = useState(room?.alias ?? "");
  const [enabled, setEnabled] = useState(room?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const saved = room
        ? await api.updateRoom(room.id, { input, alias, enabled })
        : await api.createRoom(input, alias, enabled);
      onSaved(saved);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "保存失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="dialog editor" role="dialog" aria-modal="true" aria-labelledby="editor-title">
        <div className="dialog-head">
          <div>
            <p className="section-kicker">监控目标</p>
            <h2 id="editor-title">{room ? "编辑房间" : "添加房间"}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭">×</button>
        </div>
        <form onSubmit={submit}>
          <label htmlFor="room-input">直播 URL 或 web_rid</label>
          <input
            id="room-input"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="https://live.douyin.com/123456789"
            required
          />
          <p className="field-help">不支持主播主页、unique_id 或分享短链。</p>
          <label htmlFor="alias">备注名</label>
          <input id="alias" value={alias} onChange={(event) => setAlias(event.target.value)} placeholder="例如：夜间值守" maxLength={80} />
          <label className="switch-row">
            <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
            <span>启用定时监控</span>
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="dialog-actions">
            <button type="button" className="button ghost" onClick={onClose}>取消</button>
            <button className="button primary" disabled={busy}>{busy ? "保存中…" : "保存房间"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function SessionHistory({ room, onClose }: { room: RoomRecord; onClose: () => void }) {
  const [items, setItems] = useState<LiveSession[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (next?: string | null) => {
    setLoading(true);
    setError("");
    try {
      const page = await api.listSessions(room.id, next);
      setItems((current) => next ? [...current, ...page.items] : page.items);
      setCursor(page.nextCursor);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "无法读取历史");
    } finally {
      setLoading(false);
    }
  }, [room.id]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="dialog history" role="dialog" aria-modal="true" aria-labelledby="history-title">
        <div className="dialog-head">
          <div>
            <p className="section-kicker">场次历史</p>
            <h2 id="history-title">{room.alias || room.anchorName || room.webRid}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭">×</button>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
        {!loading && items.length === 0 && <div className="empty compact">还没有记录到开播场次。</div>}
        <ol className="session-list">
          {items.map((session) => (
            <li key={session.id}>
              <span className={`session-signal ${session.endedAt ? "ended" : "active"}`} />
              <div>
                <strong>{session.title || "未命名直播"}</strong>
                <p>{formatTime(session.startedAt)} — {session.endedAt ? formatTime(session.endedAt) : "直播中"}</p>
              </div>
            </li>
          ))}
        </ol>
        {cursor && <button className="button ghost wide" disabled={loading} onClick={() => void load(cursor)}>{loading ? "读取中…" : "加载更早场次"}</button>}
      </section>
    </div>
  );
}

function Dashboard({ onLogout }: { onLogout: () => void }) {
  const [rooms, setRooms] = useState<RoomRecord[]>([]);
  const [relayHealth, setRelayHealth] = useState<RelayHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [filter, setFilter] = useState<RoomFilter>("all");
  const [editor, setEditor] = useState<EditorState>(null);
  const [historyRoom, setHistoryRoom] = useState<RoomRecord | null>(null);
  const [previewRoom, setPreviewRoom] = useState<RoomRecord | null>(null);
  const [notice, setNotice] = useState("");
  const [probingId, setProbingId] = useState<string | null>(null);

  const loadRooms = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [nextRooms, nextRelayHealth] = await Promise.all([
        api.listRooms(includeArchived),
        api.getRelayStatus(),
      ]);
      setRooms(nextRooms);
      setRelayHealth(nextRelayHealth);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        onLogout();
        return;
      }
      setNotice(cause instanceof Error ? cause.message : "无法读取房间");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [includeArchived, onLogout]);

  useEffect(() => {
    const initialTimer = window.setTimeout(() => void loadRooms(), 0);
    const timer = window.setInterval(() => void loadRooms(true), 30_000);
    return () => {
      window.clearTimeout(initialTimer);
      window.clearInterval(timer);
    };
  }, [loadRooms]);

  const activeRooms = rooms.filter((room) => !room.archivedAt);
  const counts = {
    total: activeRooms.length,
    live: activeRooms.filter((room) => room.confirmedLive).length,
    error: activeRooms.filter((room) => room.probeHealth === "error").length,
  };
  const visibleRooms = useMemo(
    () => rooms.filter((room) => {
      if (filter === "all") return true;
      if (filter === "error") return room.probeHealth === "error";
      if (filter === "live") return room.confirmedLive;
      return !room.confirmedLive;
    }),
    [filter, rooms],
  );

  function replaceRoom(room: RoomRecord) {
    setRooms((current) => {
      const next = current.some((item) => item.id === room.id)
        ? current.map((item) => item.id === room.id ? room : item)
        : [room, ...current];
      return includeArchived ? next : next.filter((item) => !item.archivedAt);
    });
  }

  async function toggleRoom(room: RoomRecord) {
    try {
      replaceRoom(await api.updateRoom(room.id, { alias: room.alias, enabled: !room.enabled }));
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "更新失败");
    }
  }

  async function archiveRoom(room: RoomRecord) {
    if (!window.confirm(`归档“${room.alias || room.anchorName || room.webRid}”？历史场次会保留。`)) return;
    try {
      replaceRoom(await api.archiveRoom(room.id));
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "归档失败");
    }
  }

  async function restoreRoom(room: RoomRecord) {
    try {
      replaceRoom(await api.restoreRoom(room.id));
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "恢复失败");
    }
  }

  async function openPreview(room: RoomRecord) {
    setProbingId(room.id);
    setNotice("");
    try {
      const receipt: ProbeRequestReceipt = await api.probeRoom(room.id);
      if (relayHealth && !relayHealth.online) {
        setNotice("即时探测已排队，但本地中继当前离线；恢复后会自动处理。");
        return;
      }

      for (let attempt = 0; attempt < 17; attempt += 1) {
        await wait(1_500);
        const latestRooms = await api.listRooms(includeArchived);
        setRooms(latestRooms);
        const refreshed = latestRooms.find((item) => item.id === room.id);
        if (!refreshed || refreshed.lastCheckedAt === receipt.baselineLastCheckedAt) continue;
        if (refreshed.probeHealth === "error") {
          setNotice(`预览刷新失败：${refreshed.lastError || "本地探测异常"}`);
        } else if (refreshed.confirmedLive) {
          setPreviewRoom(refreshed);
        } else {
          setNotice("房间当前确认未开播，已保留原平台入口。");
        }
        return;
      }
      setNotice("即时探测仍在排队；页面会继续同步结果，可稍后再次预览。");
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "预览刷新失败");
    } finally {
      setProbingId(null);
    }
  }

  async function logout() {
    await api.logout().catch(() => undefined);
    onLogout();
  }

  return (
    <main className="app-shell">
      <header className="masthead">
        <div className="brand-block">
          <div className="mini-radar" aria-hidden="true"><span /></div>
          <div><strong>LiveRadar</strong><span>直播雷达</span></div>
        </div>
        <div className="scan-line" aria-hidden="true"><i /></div>
        <button className="text-button" onClick={() => void logout()}>退出</button>
      </header>

      <section className="command-strip" aria-label="监控概况">
        <div className="command-copy">
          <p>{counts.live > 0 ? "捕获到直播信号" : "持续扫描中"}</p>
          <h1>{counts.live > 0 ? `${counts.live} 个房间正在直播` : "等待下一次开播"}</h1>
          <span>
            {relayHealth?.online ? "本地中继在线" : relayHealth ? "本地中继离线" : "正在读取中继状态"}
            {relayHealth?.lastSeenAt ? ` · 最近心跳 ${formatTime(relayHealth.lastSeenAt)}` : ""}
            {" · 列表每 30 秒同步"}
          </span>
        </div>
        <dl className="metrics">
          <div><dt>监控</dt><dd>{counts.total}</dd></div>
          <div className="live"><dt>直播</dt><dd>{counts.live}</dd></div>
          <div className="warn"><dt>异常</dt><dd>{counts.error}</dd></div>
        </dl>
      </section>

      {notice && <div className="notice" role="status"><span>{notice}</span><button onClick={() => setNotice("")} aria-label="关闭提示">×</button></div>}

      <section className="room-section">
        <div className="toolbar">
          <div className="filter-group" aria-label="状态筛选">
            {(["all", "live", "offline", "error"] as const).map((value) => (
              <button key={value} className={filter === value ? "active" : ""} onClick={() => setFilter(value)}>
                {value === "all" ? "全部" : value === "error" ? "探测异常" : value === "live" ? "直播中" : "未开播"}
              </button>
            ))}
          </div>
          <div className="toolbar-actions">
            <label className="archive-toggle"><input type="checkbox" checked={includeArchived} onChange={(event) => setIncludeArchived(event.target.checked)} />显示归档</label>
            <button className="button primary" onClick={() => setEditor({ mode: "create" })}>添加房间</button>
          </div>
        </div>

        {loading ? <div className="empty">正在读取雷达记录…</div> : visibleRooms.length === 0 ? (
          <div className="empty">
            <strong>{rooms.length ? "当前筛选没有房间" : "还没有监控目标"}</strong>
            <span>{rooms.length ? "切换状态筛选查看其他记录。" : "添加 live.douyin.com 地址，开始第一轮扫描。"}</span>
          </div>
        ) : (
          <div className="room-list">
            {visibleRooms.map((room) => (
              <article className={`room-row status-${room.status} health-${roomHealth(room, relayHealth).tone} ${room.archivedAt ? "archived" : ""}`} key={room.id}>
                <div className="room-cover">
                  {room.coverUrl ? <img src={room.coverUrl} alt="" /> : <span>{(room.alias || room.anchorName || "抖").slice(0, 1)}</span>}
                  <i className="signal-dot" aria-hidden="true" />
                </div>
                <div className="room-main">
                  <div className="room-title-line">
                    <h2>{room.alias || room.anchorName || room.webRid}</h2>
                    <span className="status-pill">{!room.lastSuccessAt ? "状态待确认" : STATUS_LABEL[room.status]}</span>
                    <span className={`health-pill ${roomHealth(room, relayHealth).tone}`}>{roomHealth(room, relayHealth).label}</span>
                    {!room.enabled && !room.archivedAt && <span className="muted-pill">已停用</span>}
                    {room.archivedAt && <span className="muted-pill">已归档</span>}
                  </div>
                  <p className="live-title">{room.title || room.anchorName || "等待直播信息"}</p>
                  <div className="room-meta">
                    <span>web_rid {room.webRid}</span>
                    <span>尝试 {formatTime(room.lastCheckedAt)}</span>
                    <span>确认 {formatTime(room.lastSuccessAt, "尚未确认")}</span>
                    {room.probeHealth === "error" && <span className="error-copy" title={room.lastError || undefined}>{room.lastError || "探测异常"}</span>}
                  </div>
                </div>
                <div className="room-actions">
                  {!room.archivedAt && <button className="button signal" disabled={probingId === room.id} onClick={() => void openPreview(room)}>{probingId === room.id ? "刷新中…" : "预览"}</button>}
                  <a className="button ghost" href={room.originUrl} target="_blank" rel="noreferrer">抖音</a>
                  <button className="button ghost" onClick={() => setHistoryRoom(room)}>历史</button>
                  {!room.archivedAt ? (
                    <details className="more-menu">
                      <summary aria-label="更多操作">•••</summary>
                      <div>
                        <button onClick={() => setEditor({ mode: "edit", room })}>编辑</button>
                        <button onClick={() => void toggleRoom(room)}>{room.enabled ? "停用" : "启用"}</button>
                        <button className="danger" onClick={() => void archiveRoom(room)}>归档</button>
                      </div>
                    </details>
                  ) : <button className="button ghost" onClick={() => void restoreRoom(room)}>恢复</button>}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {editor && <RoomEditor state={editor} onClose={() => setEditor(null)} onSaved={replaceRoom} />}
      {historyRoom && <SessionHistory room={historyRoom} onClose={() => setHistoryRoom(null)} />}
      {previewRoom && <VideoPreview room={previewRoom} onClose={() => setPreviewRoom(null)} />}
    </main>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    api.getSession()
      .then((session) => setAuthenticated(session.authenticated))
      .catch(() => setAuthenticated(false));
  }, []);

  if (authenticated === null) return <main className="boot-screen"><div className="radar-mark" aria-hidden="true"><span /></div><p>正在校准雷达…</p></main>;
  if (!authenticated) return <Login onAuthenticated={() => setAuthenticated(true)} />;
  return <Dashboard onLogout={() => setAuthenticated(false)} />;
}
